import { useState } from 'react';
import { docsApi } from '@/lib/api';

/**
 * Open a role-scoped PDF guide in a new tab.
 *
 * Shared by the sidebar Guides list and the Latest Features dialog so the two
 * cannot drift. The awkward parts are load-bearing:
 *
 *  - the endpoint needs the Bearer token in a header, so a plain <a href> can't
 *    fetch it and we won't put a JWT in a URL — hence blob + window.open;
 *  - the blank tab is opened synchronously inside the click handler, before any
 *    await, or the popup blocker kills it. If it is blocked anyway we fall back
 *    to a download.
 */
export function useOpenGuide() {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const openGuide = async (key: string) => {
    setBusy(key);
    setError(null);
    const win = window.open('', '_blank');
    try {
      const { blob } = await docsApi.fetchBlob(key);
      const url = URL.createObjectURL(blob);
      if (win && !win.closed) {
        win.location.href = url;
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = `${key}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      if (win && !win.closed) win.close();
      setError(e?.message || 'Could not open the guide');
    } finally {
      setBusy(null);
    }
  };

  return { openGuide, busy, error };
}
